import type {
  DetailAlbumDto,
  DetailCreatorDto,
  DetailTrackDto,
} from "@/lib/lutely-api/gen/dtos";
import {
  absoluteUrl,
  albumPath,
  creatorPath,
  type JsonLd,
  SITE_NAME,
} from "@/lib/seo";

/** Schema.org wants ISO 8601 durations; the API gives us whole seconds. */
export function isoDuration(seconds?: number | null): string | undefined {
  if (!seconds || seconds <= 0) return undefined;
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  return `PT${minutes}M${total % 60}S`;
}

/** Schema.org `datePublished` is a date, not a timestamp. */
function isoDate(value?: string | null): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : parsed.toISOString().slice(0, 10);
}

/** Drops undefined values so the emitted JSON-LD carries no empty properties. */
function compact(entry: Record<string, unknown>): JsonLd {
  return Object.fromEntries(
    Object.entries(entry).filter(([, value]) => value !== undefined),
  );
}

/**
 * The artist reference used inside album/track entities. Returns a bare name
 * when the artist has no slug, since a `/creators/{uuid}` URL is the
 * non-canonical twin and linking it would split the entity across two URLs.
 */
function artistRef(creator?: {
  stageName?: string | null;
  slug?: string | null;
}): JsonLd | undefined {
  if (!creator?.stageName) return undefined;
  return compact({
    "@type": "MusicGroup",
    name: creator.stageName,
    url: creator.slug ? absoluteUrl(creatorPath(creator.slug)) : undefined,
  });
}

export function creatorJsonLd(creator: DetailCreatorDto): JsonLd | null {
  if (!creator.stageName || !creator.slug) return null;

  const sameAs = (creator.socials ?? [])
    .map((social) => social.url)
    .filter((url): url is string => Boolean(url));

  return compact({
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    name: creator.stageName,
    url: absoluteUrl(creatorPath(creator.slug)),
    description: creator.bio || undefined,
    image: creator.profilePictureUrl || creator.bannerImageUrl || undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  });
}

export function albumJsonLd(album: DetailAlbumDto): JsonLd | null {
  const creatorSlug = album.creator?.slug;
  if (!album.title || !album.slug || !creatorSlug) return null;

  const url = absoluteUrl(albumPath(creatorSlug, album.slug));
  // A release dated in the future shows a countdown and a notify-me block
  // instead of a buy button, so there is no offer to advertise yet — not even
  // a PreOrder, since nothing can be ordered in advance.
  const releasesAt = album.releaseDate ? new Date(album.releaseDate) : null;
  const isPreRelease =
    releasesAt !== null &&
    !Number.isNaN(releasesAt.getTime()) &&
    releasesAt.getTime() > Date.now();
  const tracks = (album.tracks ?? [])
    .filter((track) => track.title)
    .map((track) =>
      compact({
        "@type": "MusicRecording",
        name: track.title,
        position: track.position,
        duration: isoDuration(track.duration),
        // Only tracks published standalone have a page of their own.
        url: track.published
          ? absoluteUrl(`/tracks/${track.trackId}`)
          : undefined,
      }),
    );

  return compact({
    "@context": "https://schema.org",
    "@type": "MusicAlbum",
    name: album.title,
    url,
    description: album.description || undefined,
    image: album.artworkUrl || undefined,
    byArtist: artistRef(album.creator),
    datePublished: isoDate(album.releaseDate ?? album.publishedAt),
    numTracks: tracks.length || undefined,
    track: tracks.length ? tracks : undefined,
    // `price` is the floor on pay-what-you-want releases, which is also what
    // schema.org's Offer.price means for a minimum-priced product.
    offers:
      album.price != null && !isPreRelease
        ? {
            "@type": "Offer",
            price: album.price,
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url,
            seller: { "@type": "Organization", name: SITE_NAME },
          }
        : undefined,
  });
}

export function trackJsonLd(track: DetailTrackDto): JsonLd | null {
  if (!track.title) return null;

  const album = track.albums?.[0];

  return compact({
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name: track.title,
    url: absoluteUrl(`/tracks/${track.trackId}`),
    description: track.description || undefined,
    image: track.imageUrl || undefined,
    duration: isoDuration(track.duration),
    byArtist: artistRef(track.creator),
    datePublished: isoDate(track.releaseDate ?? track.publishedAt),
    inAlbum: album?.title
      ? compact({ "@type": "MusicAlbum", name: album.title })
      : undefined,
  });
}
